import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import Button from '@ux/button';
import ButtonSet from '@ux/button-set';
import Toggle from '@ux/toggle';
import Modal from '@ux/modal';

import * as eids from '../content/eids.js';

/**
 * Modal-based view responsible for managing consents more granularly
 * for each opt-out category available to the visitor
 *
 * @param {Visitor} visitor Page viewer for which consents are updating
 * @param {string} consentType The value of the consent Type
 * @param {object} i18n The localization strings object
 * @param {boolean} open Flag to show / hide Modal
 * @param {function} setOpen To show / hide Modal based on flag state
 * @returns {View} The privacy manager module
 * @public
 */
export default function PrivacyManager({ visitor, i18n, open, setOpen }) {
  // As Privacy manager loads send event to traffic client
  visitor.tcc.addImpression(eids.privacyManager.impression);

  const checkedEssential = true;
  const { consentType, optOutCats } = visitor;

  const [categoryStates, setCategoryStates] = useState(Object.keys(optOutCats).reduce((acc, category) => {
    // automatically
    acc[category] = consentType !== 'explicit';
    return acc;
  }, {}));

  // eslint-disable-next-line no-unused-vars
  const onSubmit = useCallback(evt => {
    evt.preventDefault();

    visitor.recordInteraction({
      eid: eids.privacyManager.save,
      utag: true,
      //
      // Uses Tealium {0 => true , 1 => false} format
      //
      consents: Object.keys(categoryStates).reduce((acc, cat) => {
        const value = Boolean(categoryStates[cat]);
        acc[cat] = value ? 0 : 1;
        return acc;
      }, {})
    });

    setOpen(false);
  }, [categoryStates]);

  const onClickClose = () => {
    visitor.recordInteraction({ eid: eids.privacyManager.dismiss });
    setOpen(false);
  };

  const setChecked = (key, newValue) => {
    setCategoryStates({
      ...categoryStates,
      [key]: newValue
    });
  };

  return (
    <Modal open={ open } title={ i18n['privacyManager.title'] } onClose={ onClickClose }>
      <form onSubmit={ evt => onSubmit(evt, categoryStates) }>
        <div>
          <Modal.Body>
            <div id='privacy_view'>
              <div className='row'>
                <div className='col-xs-12'>
                  <p className='small'> { i18n['privacyManager.description'] } </p>
                </div>
              </div>
              <div className='row mb-1'>
                <div className='col-xs-12'>
                  <h4 className='headline-primary'> { i18n['privacyManager.category'] } </h4>
                </div>
              </div>
              {
                Object.keys(optOutCats).map(key => {
                  return (
                    <div className='row' key={ key }>
                      <div className='col-md-3'>
                        <p>
                          <strong> { i18n['privacyManager.' + key] } </strong>
                        </p>
                      </div>
                      <div className='col-md-6' key={ `${key}Desc` }>
                        <p className='small'> { i18n['privacyManager.' + key + 'Text'] } </p>
                      </div>
                      <div className='col-md-3'>
                        <Toggle id={ key } key={ `${key}Toggle` } checked={ categoryStates[key] }
                          onChange={ () => setChecked(key, !categoryStates[key]) } />
                      </div>
                    </div>
                  );
                })
              }
              <div id='privacy_view_chkBoxDiv'>
                <div className='row'>
                  <div className='col-md-3'>
                    <p>
                      <strong> { i18n['privacyManager.essential'] } </strong>
                    </p>
                  </div>
                  <div className='col-md-6'>
                    <p className='small'> { i18n['privacyManager.essentialText'] } </p>
                  </div>
                  <div className='col-md-3'>
                    <Toggle
                      key='privacy_view_essential'
                      checked={ checkedEssential }
                      label={ i18n['privacyManager.on'] }
                      disabled />
                  </div>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <ButtonSet>
              <Button
                id='privacy_view_confirm'
                className='ux-btn-set-item'
                design='primary'
                type='submit'
                text={ i18n['privacyManager.save'] }/>
            </ButtonSet>
          </Modal.Footer>
        </div>
      </form>
    </Modal>
  );
}

PrivacyManager.propTypes = {
  visitor: PropTypes.object.isRequired,
  i18n: PropTypes.objectOf(PropTypes.string).isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired
};
